import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuthState';

interface ChallengeData {
  challenge: any;
  leaderboard: any[];
  userProgress: any;
  referrals: any[];
  totalParticipants: number;
}

interface OptimizedChallengeDataOptions {
  challengeId: string;
  enableRealTime?: boolean;
  refetchInterval?: number;
  page?: number;
  pageSize?: number;
}

// Cache management for better performance
const CACHE_KEYS = {
  challenge: (id: string | number) => `/api/v1/challenges/${id}`,
  leaderboard: (id: string | number, page?: number, pageSize?: number) => 
    `/api/v1/challenges/${id}/leaderboard${page ? `?page=${page}&limit=${pageSize || 10}` : ''}`,
  userProgress: (id: string | number, userId?: number) => `/api/v1/challenges/${id}/progress/${userId}`,
  referrals: (id: string | number, userId?: number) => `/api/v1/challenges/${id}/referrals/${userId}`,
} as const;

// Optimized data fetcher with intelligent caching
export function useOptimizedChallengeData({
  challengeId,
  enableRealTime = true,
  refetchInterval = 45000, // 45 seconds for reasonable updates
  page = 1,
  pageSize = 10,
}: OptimizedChallengeDataOptions) {
  const { user, session } = useAuth();
  const queryClient = useQueryClient();
  const lastUpdateRef = useRef<number>(0);
  const isActiveRef = useRef(true);

  // Pause updates when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      isActiveRef.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Dynamic refetch interval that stops when document is hidden
  const getRefetchInterval = useCallback(() => {
    if (!enableRealTime || document.hidden) {
      return false;
    }
    return refetchInterval;
  }, [enableRealTime, refetchInterval]);

  // Optimized challenge data fetcher
  const { data: challengeData, isLoading: challengeLoading, error: challengeError } = useQuery({
    queryKey: [CACHE_KEYS.challenge(challengeId)],
    queryFn: async () => {
      const response = await fetch('/api/v1/challenges/active-race');
      if (!response.ok) throw new Error('Failed to fetch challenge');
      return response.json();
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - challenge data doesn't change often
    gcTime: 15 * 60 * 1000, // 15 minutes garbage collection
    refetchOnWindowFocus: false,
    retry: 2,
  });

  // Real-time leaderboard with intelligent updates
  const { data: leaderboardData, isFetching: leaderboardFetching } = useQuery({
    queryKey: [CACHE_KEYS.leaderboard(challengeId, page, pageSize)],
    queryFn: async () => {
      const response = await fetch(`/api/v1/challenges/${challengeId}/leaderboard?page=${page}&limit=${pageSize}&include_velocity=true`);
      if (!response.ok) throw new Error('Failed to fetch leaderboard');
      const data = await response.json();
      
      // Store timestamp for change detection
      lastUpdateRef.current = Date.now();
      
      return data;
    },
    enabled: !!challengeId && !!challengeData,
    refetchInterval: getRefetchInterval,
    refetchOnWindowFocus: enableRealTime,
    staleTime: refetchInterval + 5000, // Slightly more than refetch interval to prevent stacked fetches
    gcTime: 2 * refetchInterval,
    retry: 1,
    // Optimistic updates - keep previous data while fetching
    placeholderData: (previousData) => previousData,
  });

  // User progress with optimized caching
  const { data: userProgress, isFetching: userProgressFetching } = useQuery({
    queryKey: [CACHE_KEYS.userProgress(challengeId, user?.id ? Number(user.id) : undefined)],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      const response = await fetch(`/api/v1/challenges/${challengeId}/my-progress`, { headers });
      if (response.status === 404) return null;
      if (!response.ok) throw new Error('Failed to fetch user progress');
      return response.json();
    },
    enabled: !!challengeId && !!user?.id && !!session?.access_token && !!challengeData,
    refetchInterval: getRefetchInterval,
    refetchOnWindowFocus: enableRealTime,
    staleTime: refetchInterval + 5000, // Slightly more than refetch interval to prevent stacked fetches
    gcTime: 2 * refetchInterval,
    retry: 1,
    placeholderData: (previousData) => previousData,
  });

  // Referrals data with smart caching
  const { data: referralsData } = useQuery({
    queryKey: [CACHE_KEYS.referrals(challengeId, user?.id ? Number(user.id) : undefined)],
    queryFn: async () => {
      const headers: Record<string, string> = {};
      if (session?.access_token) {
        headers['Authorization'] = `Bearer ${session.access_token}`;
      }
      
      const response = await fetch(`/api/v1/challenges/referrals/status?challenge_id=${challengeId}`, { headers });
      if (response.status === 404) return [];
      if (!response.ok) throw new Error('Failed to fetch referrals');
      const data = await response.json();
      return data.referrals || [];
    },
    enabled: !!challengeId && !!user?.id && !!session?.access_token && !!challengeData,
    refetchInterval: () => enableRealTime && !document.hidden ? refetchInterval * 2 : false, // Less frequent updates
    refetchOnWindowFocus: false, // Referrals don't need immediate updates
    staleTime: refetchInterval * 2 + 5000, // Prevent stacked fetches
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    placeholderData: (previousData) => previousData,
  });

  // Prefetch related data for better UX
  const prefetchRelatedData = useCallback(() => {
    if (!challengeId || !user?.id) return;

    // Prefetch user referral code
    queryClient.prefetchQuery({
      queryKey: ['/api/v1/challenges/referral-code'],
      queryFn: async () => {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json'
        };
        
        if (session?.access_token) {
          headers['Authorization'] = `Bearer ${session.access_token}`;
        }
        
        const response = await fetch('/api/v1/challenges/referral-code', { headers });
        if (!response.ok) throw new Error('Failed to fetch referral code');
        return response.json();
      },
      staleTime: 10 * 60 * 1000, // 10 minutes - referral codes don't change
    });
  }, [challengeId, user?.id, session?.access_token, queryClient]);

  // Prefetch on mount
  useEffect(() => {
    prefetchRelatedData();
  }, [prefetchRelatedData]);

  // Optimized cache invalidation
  const invalidateUserData = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: [CACHE_KEYS.userProgress(challengeId, user?.id ? Number(user.id) : undefined)] 
    });
    queryClient.invalidateQueries({ 
      queryKey: [CACHE_KEYS.referrals(challengeId, user?.id ? Number(user.id) : undefined)] 
    });
  }, [queryClient, challengeId, user?.id]);

  const invalidateLeaderboard = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: [CACHE_KEYS.leaderboard(challengeId)] 
    });
  }, [queryClient, challengeId]);

  // Force refresh all data
  const refreshAllData = useCallback(() => {
    queryClient.invalidateQueries({ 
      queryKey: [CACHE_KEYS.challenge(challengeId)] 
    });
    invalidateLeaderboard();
    invalidateUserData();
  }, [queryClient, challengeId, invalidateLeaderboard, invalidateUserData]);

  // Optimistic updates for better UX
  const updateUserProgressOptimistically = useCallback((newProgress: Partial<any>) => {
    queryClient.setQueryData(
      [CACHE_KEYS.userProgress(challengeId, user?.id ? Number(user.id) : undefined)],
      (oldData: any) => oldData ? { ...oldData, ...newProgress } : newProgress
    );
  }, [queryClient, challengeId, user?.id]);

  // Extract and transform data
  const challenge = challengeData?.challenge ? {
    ...challengeData.challenge,
    id: challengeData.challenge.id?.toString() || challengeId,
    targetValue: challengeData.challenge.target || challengeData.challenge.targetValue || 1000,
    totalParticipants: challengeData.challenge.total_participants || challengeData.challenge.totalParticipants || 0,
  } : null;

  const leaderboard = leaderboardData?.leaderboard || [];
  const totalParticipants = leaderboardData?.meta?.total_participants || challenge?.totalParticipants || 0;
  const leaderboardMeta = leaderboardData?.meta;

  // Loading states
  const isInitialLoading = challengeLoading;
  const isRefreshing = leaderboardFetching || userProgressFetching;
  const hasError = !!challengeError;

  // Performance metrics
  const lastUpdate = lastUpdateRef.current;
  const timeSinceLastUpdate = lastUpdate ? Date.now() - lastUpdate : 0;

  return {
    // Data
    challenge,
    leaderboard,
    leaderboardMeta,
    userProgress,
    referrals: referralsData || [],
    totalParticipants,
    
    // Loading states
    isInitialLoading,
    isRefreshing,
    hasError,
    error: challengeError,
    
    // Actions
    refreshAllData,
    invalidateUserData,
    invalidateLeaderboard,
    updateUserProgressOptimistically,
    
    // Performance info
    lastUpdate,
    timeSinceLastUpdate,
    
    // Helper flags
    hasJoined: !!userProgress,
    isLive: challenge?.status === 'live' || challenge?.status === 'active',
  };
}

// Hook for optimized challenge list
export function useOptimizedChallengeList() {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['/api/v1/challenges/list'],
    queryFn: async () => {
      const response = await fetch('/api/v1/challenges/active-race');
      if (!response.ok) throw new Error('Failed to fetch challenges');
      return response.json();
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2,
  });

  const refreshChallenges = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['/api/v1/challenges/list'] });
  }, [queryClient]);

  return {
    challenges: data ? [data.challenge].filter(Boolean) : [],
    isLoading,
    error,
    refreshChallenges,
  };
}

// Performance monitoring hook
export function useChallengePerformanceMonitor() {
  const queryClient = useQueryClient();
  
  const getQueryStats = useCallback(() => {
    const queryCache = queryClient.getQueryCache();
    const queries = queryCache.getAll();
    
    const challengeQueries = queries.filter(query => 
      query.queryKey.some(key => 
        typeof key === 'string' && key.includes('/api/v1/challenges')
      )
    );

    return {
      totalQueries: challengeQueries.length,
      activeQueries: challengeQueries.filter(q => q.state.fetchStatus === 'fetching').length,
      cachedQueries: challengeQueries.filter(q => q.state.status === 'success').length,
      errorQueries: challengeQueries.filter(q => q.state.status === 'error').length,
    };
  }, [queryClient]);

  const clearStaleCache = useCallback(() => {
    queryClient.clear();
  }, [queryClient]);

  return {
    getQueryStats,
    clearStaleCache,
  };
}
